/**
 * Unit models as cached pictures (ADR-0075).
 *
 * The map stays Canvas 2D. Each model is rendered once — per unit type, owner's colour,
 * facing and size — by one shared off-screen WebGL renderer, from the 3D view's own angle
 * (looking down at 30°, so the ground squashes by SQUASH = 0.5), cropped tight, and kept.
 * The dimetric renderer then draws the picture like any other. Without WebGL, or for a
 * unit with no model, `unitSprite` returns null and the disc is drawn instead.
 */

import * as T from 'three';
import { MODELS } from './units.js';
import { buildCity, type CitySpec } from './city.js';

export type { CitySpec } from './city.js';

/** Screen pixels per model unit, per pixel of tile size: a hex (26 model units across) fits a tile. */
const MODEL_SCALE = 1.0 / 13;
const ELEVATION = Math.asin(0.5);
/** A three-quarter turn, so a unit facing right is seen from its near side, not flat in profile. */
const YAW = -0.55;
/** Cities are turned a little, so streets do not line up with the screen. */
const CITY_YAW = 0.25;
/** The longest a model may be, in model units (a hex is 26 across). */
const MAX_FOOTPRINT = 24;
const CACHE_LIMIT = 900;
/** The off-screen canvas models are drawn on, in pixels square; no picture is larger. */
const CANVAS = 1024;

export interface UnitSprite {
  image: HTMLCanvasElement;
  /** Where the model's ground centre falls in the image, in image pixels. */
  anchorX: number;
  anchorY: number;
  /** Image pixels per screen pixel (the device pixel ratio it was drawn for). */
  ratio: number;
  /** The tile size it was drawn for: scale by (tile size now) / this to draw it. */
  tile: number;
}

/**
 * Models are drawn at a few tile sizes only and scaled between them (Phase 15): drawing a
 * fresh one for every zoom step stalled big games while zooming.
 */
const SIZES = [24, 34, 48, 68, 96, 136];
function sizeFor(tileSize: number): number {
  for (const s of SIZES) if (s >= tileSize) return s;
  return SIZES[SIZES.length - 1]!;
}

/**
 * New pictures are made within a time budget each frame (Phase 15): past it, a model not
 * yet drawn shows as its disc until a later frame, so one frame never stalls on hundreds.
 */
const FRAME_BUDGET_MS = 10;
let frameSpent = 0;
let waiting = false;

/** Call at the start of each frame. */
export function beginModelFrame(): void {
  frameSpent = 0;
  waiting = false;
}

/** Whether some model was skipped this frame for want of time: draw again soon. */
export function modelsWaiting(): boolean {
  return waiting;
}

let renderer: T.WebGLRenderer | null | undefined;
const scene = new T.Scene();
const cache = new Map<string, UnitSprite | null>();

function rendererOrNull(): T.WebGLRenderer | null {
  if (renderer !== undefined) return renderer;
  try {
    renderer = new T.WebGLRenderer({ antialias: true, alpha: true, canvas: document.createElement('canvas') });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(1);
    renderer.setSize(CANVAS, CANVAS, false);
    scene.add(new T.HemisphereLight(0xffffff, 0x8a8070, 1.5));
    const sun = new T.DirectionalLight(0xffffff, 1.9);
    sun.position.set(-6, 14, 9);
    scene.add(sun);
  } catch {
    renderer = null;
  }
  return renderer;
}

export function hasModel(key: string): boolean {
  return key in MODELS;
}

/**
 * The picture of a unit type in an owner's colour, facing left or right, for a tile of
 * `tileSize` screen pixels — or null when it should be drawn as a disc.
 */
export function unitSprite(key: string, colour: string, facingLeft: boolean, tileSize: number): UnitSprite | null {
  const build = MODELS[key];
  if (!build) return null;
  return cached(`u|${key}|${facingLeft ? 'L' : 'R'}`, build, colour, facingLeft ? Math.PI - YAW : YAW, true, tileSize);
}

/** The picture of a city (ADR-0076), or null when it should be drawn the old way. */
export function citySprite(spec: CitySpec, colour: string, tileSize: number): UnitSprite | null {
  const id = `c|${spec.seed}|${Math.min(spec.population, 20)}|${spec.era}|${spec.capital ? 1 : 0}|${spec.buildings.join(',')}`;
  return cached(id, (g, civ) => buildCity(g, civ, spec), colour, CITY_YAW, false, tileSize);
}

function cached(
  key: string,
  build: (g: T.Group, civ: number) => void,
  colour: string,
  yaw: number,
  centred: boolean,
  tileSize: number,
): UnitSprite | null {
  const ratio = Math.min(2, window.devicePixelRatio || 1);
  const step = sizeFor(tileSize);
  const id = `${key}|${colour}|${step}|${ratio}`;
  if (cache.has(id)) return cache.get(id)!;
  if (frameSpent > FRAME_BUDGET_MS) {
    waiting = true;
    return null;
  }
  const gl = rendererOrNull();
  let sprite: UnitSprite | null = null;
  if (gl) {
    const started = performance.now();
    try {
      sprite = draw(gl, build, colour, yaw, centred, step, ratio);
    } catch {
      sprite = null;
    }
    frameSpent += performance.now() - started;
  }
  if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value!);
  cache.set(id, sprite);
  return sprite;
}

function draw(
  gl: T.WebGLRenderer,
  build: (g: T.Group, civ: number) => void,
  colour: string,
  yaw: number,
  centred: boolean,
  tileSize: number,
  ratio: number,
): UnitSprite {
  const model = new T.Group();
  build(model, new T.Color(colour).getHex());
  // Stand the model on its footprint's centre, then turn it to face its way.
  const footprint = new T.Box3().setFromObject(model);
  const centre = footprint.getCenter(new T.Vector3());
  const holder = new T.Group();
  // Nothing spills far onto neighbouring tiles: long ships and planes are shrunk to fit.
  const size = footprint.getSize(new T.Vector3());
  const fit = Math.min(1, MAX_FOOTPRINT / Math.max(size.x, size.z));
  model.scale.setScalar(fit);
  if (centred) model.position.set(-centre.x * fit, 0, -centre.z * fit);
  holder.add(model);
  holder.rotation.y = yaw;
  scene.add(holder);
  holder.updateMatrixWorld(true);

  // Crop to the model as the camera sees it: view x = world x, view up = y·cos e − z·sin e.
  const box = new T.Box3().setFromObject(holder);
  let left = Infinity;
  let right = -Infinity;
  let bottom = Infinity;
  let top = -Infinity;
  for (const x of [box.min.x, box.max.x])
    for (const y of [box.min.y, box.max.y])
      for (const z of [box.min.z, box.max.z]) {
        const up = y * Math.cos(ELEVATION) - z * Math.sin(ELEVATION);
        left = Math.min(left, x);
        right = Math.max(right, x);
        bottom = Math.min(bottom, up);
        top = Math.max(top, up);
      }
  const pad = 0.6;
  left -= pad;
  right += pad;
  bottom -= pad;
  top += pad;

  const scale = Math.min(tileSize * MODEL_SCALE * ratio, CANVAS / Math.max(right - left, top - bottom));
  const width = Math.max(1, Math.min(CANVAS, Math.ceil((right - left) * scale)));
  const height = Math.max(1, Math.min(CANVAS, Math.ceil((top - bottom) * scale)));
  const camera = new T.OrthographicCamera(left, left + width / scale, top, top - height / scale, -500, 500);
  camera.position.set(0, Math.sin(ELEVATION) * 100, Math.cos(ELEVATION) * 100);
  camera.lookAt(0, 0, 0);
  // lookAt keeps view x on world x for this camera, so the crop above lines up.

  // One fixed-size canvas, drawn into its top-left corner: resizing a WebGL canvas for
  // every model was most of the cost of making one (Phase 15). WebGL counts rows from
  // the bottom, hence CANVAS - height.
  gl.setViewport(0, CANVAS - height, width, height);
  gl.setScissor(0, CANVAS - height, width, height);
  gl.setScissorTest(true);
  gl.clear();
  gl.render(scene, camera);

  const image = document.createElement('canvas');
  image.width = width;
  image.height = height;
  image.getContext('2d')!.drawImage(gl.domElement, 0, 0, width, height, 0, 0, width, height);

  scene.remove(holder);
  model.traverse((o) => {
    if (o instanceof T.Mesh) o.geometry.dispose();
  });
  return { image, anchorX: -left * scale, anchorY: top * scale, ratio, tile: tileSize };
}

// ─── facing ──────────────────────────────────────────────────────────────────────

/** Which way each unit last moved, remembered by the renderer: units face their travel. */
const lastSeen = new Map<number, { x: number; left: boolean }>();

export function facesLeft(unitId: number, worldX: number): boolean {
  const seen = lastSeen.get(unitId);
  if (!seen) {
    lastSeen.set(unitId, { x: worldX, left: false });
    return false;
  }
  if (worldX !== seen.x) {
    seen.left = worldX < seen.x;
    seen.x = worldX;
  }
  return seen.left;
}
